<template>
  <!--
    Three-up portrait result gallery used by AvatarPicker's Simple
    and Advanced tabs. Each portrait is a clickable card; clicking
    one emits ``pick`` with the {portrait_url, pool_id} payload.
    The "Refine & try again" link discards the triptych state in
    the parent (the actual pool rows stay alive — unpicked siblings
    remain browsable for everyone).
  -->
  <div class="triptych">
    <div class="triptych-grid">
      <button
        v-for="p in portraits"
        :key="p.pool_id || p.portrait_url"
        class="result-card"
        :class="{ saving: savingPoolId === p.pool_id }"
        :disabled="claiming"
        @click="$emit('pick', p)"
      >
        <img :src="p.portrait_url" alt="generated portrait" class="result-img" />
        <span v-if="savingPoolId === p.pool_id" class="saving-overlay">
          Saving…
        </span>
      </button>
    </div>
    <div class="triptych-actions">
      <button class="ghost-btn" @click="$emit('refine')">
        Refine &amp; try again
      </button>
      <button
        v-if="compiledPrompt"
        class="ghost-btn"
        @click="showPrompt = !showPrompt"
      >
        {{ showPrompt ? 'Hide prompt' : 'View prompt' }}
      </button>
    </div>
    <pre v-if="showPrompt" class="prompt-pre">{{ compiledPrompt }}</pre>
    <p class="hint">
      <template v-if="portraits.length > 1">
        Pick the one you like. The others stay in the public portrait
        library for other Mystics to discover.
      </template>
      <template v-else>
        Click to preview. Refine if it's not quite right - discarded
        portraits go into the public library for other Mystics.
      </template>
    </p>
  </div>
</template>

<script setup>
import { ref } from 'vue'

defineProps({
  portraits: { type: Array, default: () => [] },
  savingPoolId: { type: [Number, String, null], default: null },
  claiming: { type: Boolean, default: false },
  compiledPrompt: { type: String, default: '' },
})
defineEmits(['pick', 'refine'])

const showPrompt = ref(false)
</script>

<style scoped>
.triptych {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}
.triptych-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
@media (max-width: 640px) {
  .triptych-grid { grid-template-columns: 1fr; }
}
.result-card {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  aspect-ratio: 1;
  border: 2px solid rgba(201, 169, 78, 0.3);
  background: rgba(0, 0, 0, 0.4);
  cursor: pointer;
  padding: 0;
  transition: all 0.18s;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}
.result-card:hover:not(:disabled) {
  border-color: #C9A94E;
  transform: scale(1.02);
  box-shadow: 0 6px 22px rgba(201, 169, 78, 0.22);
}
.result-card:disabled { cursor: not-allowed; }
.result-card.saving {
  border-color: rgb(120, 220, 150);
  box-shadow: 0 0 22px rgba(80, 200, 120, 0.32);
}
.result-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.saving-overlay {
  position: absolute;
  inset: 0;
  background: rgba(8, 0, 15, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.triptych-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.ghost-btn {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  color: rgba(212, 196, 232, 0.75);
  font-size: 12px;
  padding: 6px 14px;
  cursor: pointer;
  font-family: inherit;
}
.ghost-btn:hover {
  border-color: #C9A94E;
  color: #C9A94E;
}
.prompt-pre {
  white-space: pre-wrap;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 13px;
  color: rgba(232, 216, 255, 0.7);
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(195, 169, 255, 0.16);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 0;
  max-height: 180px;
  overflow-y: auto;
}
.hint {
  text-align: center;
  font-size: 12px;
  color: rgba(212, 196, 232, 0.5);
  font-style: italic;
  margin: 4px 0 0;
}
</style>
